import { useState, useEffect, useRef } from 'react';
import { useGameState } from '../../../net/GameProvider.js';
import { useI18n } from '../../../i18n.js';
import { CardUI } from '../../CardUI.js';
import { SUIT_SYMBOL, winningMuushigPlay, type MuushigTrickCard } from '@big2/shared';
import { getPlayerPosition } from '../common/TablePosition.js';

interface MuushigTableProps {
  isPaused: boolean;
  canExchangeStock?: boolean;
  canTakeTrump?: boolean;
  onExchangeStock?: () => void;
  onTakeTrump?: () => void;
}

const SUIT_NAMES_MN: Record<string, string> = {
  S: 'Гил',
  H: 'Бундан',
  D: 'Дөрвөлжин',
  C: 'Цэцэг',
};

const SUIT_NAMES_EN: Record<string, string> = {
  S: 'Spades',
  H: 'Hearts',
  D: 'Diamonds',
  C: 'Clubs',
};

export function MuushigTable({
  isPaused,
  canExchangeStock = false,
  canTakeTrump = false,
  onExchangeStock,
  onTakeTrump,
}: MuushigTableProps) {
  const { state } = useGameState();
  const { lang } = useI18n();
  const isMn = lang === 'mn';
  const { game } = state;

  const [visualTrick, setVisualTrick] = useState<{
    cards: MuushigTrickCard[];
    winnerId: string;
    isCollecting: boolean;
  } | null>(null);

  const lastTrick = game?.muushig?.lastTrick;
  const lastTrickKey = lastTrick && lastTrick.cards.length > 0
    ? `${lastTrick.winnerPlayerId}:${lastTrick.cards.map((c) => `${c.seat}-${c.card}`).join(',')}`
    : null;

  const lastHandledTrickKeyRef = useRef<string | null>(null);
  const prevTrickCardsLengthRef = useRef<number>((game?.muushig?.trickCards ?? []).length);

  // If a new trick has started with fresh cards played, dismiss any lingering visual trick
  useEffect(() => {
    const currentTrick = game?.muushig?.trickCards ?? [];
    if (currentTrick.length > 0 && visualTrick) {
      setVisualTrick(null);
    }
  }, [game?.muushig?.trickCards, visualTrick]);

  useEffect(() => {
    const currentTrick = game?.muushig?.trickCards ?? [];
    const prevTrickLength = prevTrickCardsLengthRef.current;
    prevTrickCardsLengthRef.current = currentTrick.length;

    // Detect when a trick just completed and was cleared into lastTrick
    if (
      lastTrickKey &&
      lastTrickKey !== lastHandledTrickKeyRef.current &&
      lastTrick &&
      lastTrick.cards.length > 0 &&
      (prevTrickLength > 0 || currentTrick.length === 0)
    ) {
      lastHandledTrickKeyRef.current = lastTrickKey;

      const winnerId = lastTrick.winnerPlayerId;
      const cards = [...lastTrick.cards];

      // Step 1: Render all trick cards in place on the desk (including the last card played)
      setVisualTrick({
        cards,
        winnerId,
        isCollecting: false,
      });

      const speed = game?.gameSpeed ?? 'normal';
      // Step 2: Keep cards on the desk for ~1.8 seconds so players can see the last card played and who won
      const displayDuration = speed === 'relaxed' ? 2400 : speed === 'fast' ? 1400 : 1800;
      const sweepDuration = speed === 'relaxed' ? 1000 : speed === 'fast' ? 600 : 850;

      // Step 3: Trigger the sweep-to-winner animation to fly cards to the trick winner's seat
      const collectTimer = setTimeout(() => {
        setVisualTrick((prev) => (prev ? { ...prev, isCollecting: true } : null));
      }, displayDuration);

      // Step 4: After sweep completes, remove the visual trick from the table
      const cleanupTimer = setTimeout(() => {
        setVisualTrick(null);
      }, displayDuration + sweepDuration);

      return () => {
        clearTimeout(collectTimer);
        clearTimeout(cleanupTimer);
      };
    }
  }, [lastTrickKey, lastTrick, game?.gameSpeed]);

  if (!game || !game.muushig) return null;

  const muushig = game.muushig;
  const isPlayingStage = muushig.stage === 'playing';
  const showStockDeck = !isPlayingStage && muushig.stockCount > 0;
  const hasTopOpponent = game.players.length === 2 || game.players.length === 4 || game.players.length === 5;
  const activeTrickToRender: readonly MuushigTrickCard[] = visualTrick ? visualTrick.cards : muushig.trickCards;
  const isRedTrump = muushig.trumpSuit === 'H' || muushig.trumpSuit === 'D';

  return (
    <>
      {/* 1. Permanent, Stationary Trump & Pot Bar (anchored right below top opponent) */}
      <div className={`muushig-permanent-trump-bar${hasTopOpponent ? ' has-top-opponent' : ''}`}>
        <div
          className={`muushig-trump-plaque${canTakeTrump ? ' is-actionable' : ''}`}
          onClick={canTakeTrump ? onTakeTrump : undefined}
          role={canTakeTrump ? 'button' : undefined}
          tabIndex={canTakeTrump ? 0 : undefined}
          title={
            canTakeTrump
              ? (isMn ? 'Хөзрийн тамгыг авах (Дарна уу)' : 'Take trump card (Click to take)')
              : (isMn ? `Хөзөр (Тамга): ${SUIT_NAMES_MN[muushig.trumpSuit]}` : `Trump: ${SUIT_NAMES_EN[muushig.trumpSuit]}`)
          }
        >
          <div className="muushig-trump-mini-card">
            <CardUI
              faceDown={isPaused}
              cardCode={isPaused ? undefined : muushig.trumpCard}
            />
          </div>
          <div className="trump-plaque-text">
            <span className="trump-plaque-label">{isMn ? 'Хөзөр' : 'Trump'}</span>
            <span className="trump-plaque-symbol" style={{ color: isRedTrump ? '#f87171' : '#f1f5f9' }}>
              {isPaused ? '?' : SUIT_SYMBOL[muushig.trumpSuit]}
            </span>
            <span className="trump-plaque-name">
              {isPaused ? '' : isMn ? SUIT_NAMES_MN[muushig.trumpSuit] : SUIT_NAMES_EN[muushig.trumpSuit]}
            </span>
          </div>
          {canTakeTrump && (
            <span className="trump-plaque-take-tag">
              {isMn ? 'Авах' : 'Take'}
            </span>
          )}
        </div>

        {/* Visual 3D Card Pile Deck for Stock - disappears once the game starts (playing stage) */}
        {showStockDeck && (
          <div
            className={`muushig-stock-deck muushig-stock-pill${canExchangeStock ? ' is-actionable' : ''}`}
            onClick={canExchangeStock ? onExchangeStock : undefined}
            role={canExchangeStock ? 'button' : undefined}
            tabIndex={canExchangeStock ? 0 : undefined}
            title={
              canExchangeStock
                ? (isMn ? 'Газрын модноос солих (Дарна уу)' : 'Swap cards from stock pile (Click to swap)')
                : (isMn ? `Газрын мод: ${muushig.stockCount} үлдсэн` : `Stock cards: ${muushig.stockCount} left`)
            }
          >
            <div className="stock-deck-cards" aria-hidden="true">
              {muushig.stockCount > 2 && (
                <div className="stock-deck-layer layer-3">
                  <CardUI faceDown />
                </div>
              )}
              {muushig.stockCount > 1 && (
                <div className="stock-deck-layer layer-2">
                  <CardUI faceDown />
                </div>
              )}
              <div className="stock-deck-layer layer-1">
                <CardUI faceDown />
              </div>
            </div>
            <div className="stock-deck-info">
              <span className="stock-deck-label stock-pill-label">{isMn ? 'Газар' : 'Stock'}</span>
              <div className="stock-deck-count-row">
                <span className="stock-deck-count stock-pill-count">{muushig.stockCount}</span>
                <span className="stock-deck-unit">{isMn ? 'мод' : 'left'}</span>
              </div>
            </div>
            {canExchangeStock && (
              <span className="stock-deck-action-hint">
                {isMn ? `0-${Math.min(5, muushig.stockCount)} солих` : `Swap 0-${Math.min(5, muushig.stockCount)}`}
              </span>
            )}
          </div>
        )}
      </div>

      <div className={`game-table-center${hasTopOpponent ? ' has-top-opponent' : ''}`}>
        {/* Permanent table center watermark */}
        <img
          src="/card-table-center.svg"
          className="table-center-watermark"
          alt=""
          aria-hidden="true"
        />

        <div className={`muushig-table-center${isPlayingStage ? ' is-playing-stage' : ''}${hasTopOpponent ? ' has-top-opponent' : ''}`}>
          {/* 2. Dedicated Trick Cards Arena (Fixed min-height so trick card changes never move the table layout) */}
          <div className="muushig-trick-arena">
          {activeTrickToRender.length > 0 && (
            <div
              className={`trick-card-fan muushig-trick-cards${
                visualTrick?.isCollecting
                  ? ` trick-clearing-container sweep-to-${getPlayerPosition(visualTrick.winnerId, game.myPlayerId, game.players)}`
                  : ''
              }`}
            >
              {(() => {
                const best = winningMuushigPlay(activeTrickToRender, muushig.trumpSuit);
                const winnerPlayer = game.players.find((p) => p.playerId === best.playerId);
                const winnerName = winnerPlayer?.name ?? '';
                const total = activeTrickToRender.length;

                return (
                  <>
                    <div className="muushig-fan-row">
                      {activeTrickToRender.map((tc, idx) => {
                        const isWinning = best.playerId === tc.playerId;
                        const rot = (idx - (total - 1) / 2) * 3.6;
                        const isLatest = idx === total - 1 && !visualTrick?.isCollecting;
                        const origin = getPlayerPosition(tc.playerId, game.myPlayerId, game.players);

                        return (
                          <div
                            key={`${tc.playerId}-${tc.card}`}
                            className={`muushig-play ${idx > 0 ? 'trick-card-overlap' : ''}${isWinning ? ' is-winner' : ''}`}
                            style={{
                              position: 'relative',
                              zIndex: isWinning ? 25 : idx + 1,
                              transform: isWinning
                                ? `translateY(-6px) rotate(${rot}deg)`
                                : `rotate(${rot}deg)`,
                              transition: 'transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 240ms ease',
                            }}
                          >
                            <div className={isLatest ? `trick-play-snap trick-play-from-${origin}` : undefined}>
                              <CardUI
                                faceDown={isPaused}
                                cardCode={isPaused ? undefined : tc.card}
                                className="trick-card"
                                style={{
                                  boxShadow: isWinning
                                    ? '0 0 0 2.5px #fbbf24, 0 10px 24px rgba(251, 191, 36, 0.5), 0 4px 12px rgba(0, 0, 0, 0.5)'
                                    : '0 4px 12px rgba(0, 0, 0, 0.45)',
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Only the name of the biggest card owner */}
                    {!isPaused && winnerName && (
                      <div className="trick-label trick-player-name is-winner" title={winnerName}>
                        👑 {winnerName}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
